import { isDefined } from "@clipboard-health/util-ts";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { logEvent } from "@src/appV2/lib/analytics";
import { useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";
import { OmnisearchProvider } from "@src/appV2/Omnisearch";
import { type ShiftSearchResult } from "@src/appV2/Omnisearch/api/useSearchShift";
import { type WorkerShift } from "@src/appV2/Omnisearch/api/useGetWorkerShifts";
import { type ShiftAction } from "@src/appV2/Omnisearch/components/ShiftActionItem";
import { routes } from "@src/appV2/redesign/utils/routes";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { ADMIN_EVENTS } from "@src/constants/firebaseEvents";
import { pseudo } from "@src/modules/session";

export function ConnectedOmnisearch() {
  const dispatch = useDispatch();
  const history = useHistory();
  const session = useSelector(
    (state: { session: { admin: boolean; userId: string; adminUserId?: string } }) => state.session
  );
  const isAdmin = session?.admin;
  const isInPseudoSession =
    isAdmin && isDefined(session?.adminUserId) && session.userId !== session.adminUserId;
  const [, addToRecent] = useRecentlyVisited();

  const handleSelectAgent = useCallback(
    (agent: Agent) => {
      dispatch(pseudo({ userId: agent.userId }, false));
      addToRecent({ id: agent.userId, title: agent.name, type: "AGENT" });
      logEvent(ADMIN_EVENTS.SELECT_HCP, { id: agent.userId });
    },
    [dispatch, addToRecent]
  );

  const handleSelectFacility = useCallback(
    (facility: Facility) => {
      dispatch(pseudo({ userId: facility.userId }, false));
      addToRecent({ id: facility.userId, title: facility.name, type: "FACILITY" });
      logEvent(ADMIN_EVENTS.SELECT_HCF, { id: facility.userId });
    },
    [dispatch, addToRecent]
  );

  const handleSelectShift = useCallback(
    async (shift: ShiftSearchResult) => {
      const targetPath = `${routes.adminShifts}?shiftId=${shift.id}`;

      if (isInPseudoSession && isDefined(session.adminUserId)) {
        await dispatch(pseudo({ userId: session.adminUserId }, false));
        history.replace(targetPath);
        window.location.reload();
      } else {
        window.location.href = targetPath;
      }
    },
    [isInPseudoSession, session.adminUserId, dispatch, history]
  );

  const handleSelectShiftAction = useCallback(
    (shift: ShiftSearchResult, action: ShiftAction) => {
      if (action === "workplace" && isDefined(shift.facility?.userId)) {
        dispatch(pseudo({ userId: shift.facility.userId }, false));
        addToRecent({ id: shift.facility.userId, title: shift.facility.name, type: "FACILITY" });
        logEvent(ADMIN_EVENTS.SELECT_HCF, {
          id: shift.facility.userId,
          source: "omnisearch_shift",
        });
      } else if (action === "worker" && isDefined(shift.agent?.userId)) {
        dispatch(pseudo({ userId: shift.agent.userId }, false));
        addToRecent({
          id: shift.agent.userId,
          title: shift.agent.name ?? "Worker",
          type: "AGENT",
        });
        logEvent(ADMIN_EVENTS.SELECT_HCP, { id: shift.agent.userId, source: "omnisearch_shift" });
      }
    },
    [dispatch, addToRecent]
  );

  const handleSelectAgentShift = useCallback(
    async (workerShift: WorkerShift) => {
      const targetPath = `${routes.adminShifts}?shiftId=${workerShift.id}`;

      if (isInPseudoSession && isDefined(session.adminUserId)) {
        await dispatch(pseudo({ userId: session.adminUserId }, false));
        history.replace(targetPath);
        window.location.reload();
      } else {
        window.location.href = targetPath;
      }
    },
    [isInPseudoSession, session.adminUserId, dispatch, history]
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <OmnisearchProvider
      onSelectAgent={handleSelectAgent}
      onSelectFacility={handleSelectFacility}
      onSelectShift={handleSelectShift}
      onSelectShiftAction={handleSelectShiftAction}
      onSelectAgentShift={handleSelectAgentShift}
    />
  );
}
